import React, { useState, useEffect } from 'react';
import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/industries.css"; // Updated stylesheet
import background from "../assets/images/industriesBackdrop.jpeg"; // Placeholder for backdrop image
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faIndustry, 
  faHospital, 
  faGraduationCap, 
  faGavel, 
  faMoneyBill, 
  faBuilding, 
  faCar, 
  faPlane, 
  faShoppingCart, 
  faLaptop, 
  faArrowRight, 
  faCheck 
} from '@fortawesome/free-solid-svg-icons';

const Industries = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    setIsVisible(true);
    
    // Add schema markup for industries
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.innerHTML = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ProfessionalService",
      "name": "Colee Software Services LLC",
      "description": "Tailored software solutions for various industries",
      "serviceType": ["Software Development", "Web Development", "IT Consulting"],
      "industry": ["Healthcare", "Education", "Legal", "Finance", "Manufacturing", "Automotive", "Aerospace", "Retail", "Real Estate", "Technology"]
    });
    document.head.appendChild(script);
    
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const industries = [
    { 
      icon: faHospital, 
      title: "Healthcare", 
      description: "Innovative software solutions for patient care, medical records, and healthcare management systems.",
      technologies: "HIPAA Compliance, EHR Integration, Telemedicine, Data Security",
      category: "services"
    },
    { 
      icon: faGraduationCap, 
      title: "Education", 
      description: "Custom e-learning platforms, student management systems, and educational tools.",
      technologies: "LMS Development, Gamification, Accessibility, Cloud Solutions",
      category: "services"
    },
    { 
      icon: faGavel, 
      title: "Legal", 
      description: "Secure case management, document automation, and client communication tools for law firms.",
      technologies: "Encryption, Document Management, Billing Systems, Compliance",
      category: "services"
    },
    { 
      icon: faMoneyBill, 
      title: "Finance", 
      description: "Robust financial software for banking, investments, and secure transactions.",
      technologies: "Blockchain, PCI Compliance, Fraud Detection, FinTech APIs",
      category: "services"
    },
    { 
      icon: faIndustry, 
      title: "Manufacturing", 
      description: "Industrial automation, supply chain management, and production tracking solutions.",
      technologies: "IoT Integration, ERP Systems, Process Automation, Data Analytics",
      category: "industrial"
    },
    { 
      icon: faCar, 
      title: "Automotive", 
      description: "Software for dealership management, vehicle diagnostics, and customer service platforms.",
      technologies: "CRM Systems, Diagnostic Tools, Inventory Management, Mobile Apps",
      category: "industrial"
    },
    { 
      icon: faPlane, 
      title: "Aerospace", 
      description: "Advanced systems for flight operations, maintenance tracking, and compliance management.",
      technologies: "Safety Systems, Real-Time Data, Regulatory Compliance, Simulation",
      category: "industrial"
    },
    { 
      icon: faShoppingCart, 
      title: "Retail & E-Commerce", 
      description: "End-to-end solutions for online stores, inventory, and customer engagement.",
      technologies: "Shopping Carts, Payment Gateways, SEO Tools, Customer Analytics",
      category: "commerce"
    },
    { 
      icon: faBuilding, 
      title: "Real Estate", 
      description: "Property management software, virtual tours, and client acquisition tools.",
      technologies: "MLS Integration, CRM Platforms, 3D Visualization, Mobile Apps",
      category: "commerce"
    },
    { 
      icon: faLaptop, 
      title: "Technology", 
      description: "Cutting-edge solutions for tech startups, SaaS products, and IT infrastructure.",
      technologies: "Cloud Computing, API Development, Cybersecurity, Scalable Architecture",
      category: "innovation"
    }
  ];

  const categories = [
    { id: "all", name: "All Industries" },
    { id: "services", name: "Services" },
    { id: "industrial", name: "Industrial" },
    { id: "commerce", name: "Commerce" },
    { id: "innovation", name: "Innovation" }
  ];

  const filteredIndustries = activeTab === "all" 
    ? industries 
    : industries.filter(industry => industry.category === activeTab);

  return (
    <main className="industries-container">
      {/* Background Image */}
      <div 
        className="industries-background-image"
        style={{
          backgroundImage: `url(${background})`,
          backgroundSize: "cover",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: -1,
          filter: "brightness(0.4)"
        }}
        role="img"
        aria-label="Industries backdrop"
      />

      {/* Hero Section */}
      <section className="industries-header py-5" aria-labelledby="industries-title">
        <div className="container text-center py-5">
          <h1 
            id="industries-title" 
            className="industries-title display-3 fw-bold mb-3"
          >
            Industries We Serve
          </h1>
          <p className="industries-lead fs-4 mb-5 mx-auto">
            Tailored software solutions for diverse business sectors across the globe.
          </p>
        </div>
      </section>

      {/* Industries Section */}
      <section className="py-5" aria-labelledby="industries-section-title">
        <div className="container py-4">
          {/* Category Tabs */}
          <ul className="nav nav-pills justify-content-center mb-5" id="industryTabs" role="tablist">
            {categories.map(category => (
              <li className="nav-item mx-1 mb-2" key={category.id} role="presentation">
                <button 
                  className={`nav-link ${activeTab === category.id ? 'active' : ''}`} 
                  onClick={() => setActiveTab(category.id)}
                  style={{
                    borderRadius: "4px",
                    transition: "all 0.3s ease",
                    background: activeTab === category.id ? "linear-gradient(135deg, #02e0f8, #d5e8ee)" : "transparent",
                    color: activeTab === category.id ? "white" : "#ffffff",
                    border: `1px solid ${activeTab === category.id ? "transparent" : "rgba(255,255,255,0.3)"}`,
                    fontWeight: activeTab === category.id ? "800" : "normal",
                    padding: "0.5rem 1rem",
                    fontFamily: activeTab === category.id ? "'Orbitron', sans-serif" : "'Arial', sans-serif"
                  }}
                >
                  {category.name}
                </button>
              </li>
            ))}
          </ul>

          {/* Industry Cards */}
          <div className="row g-4">
            {filteredIndustries.map((industry, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <div className="industry-card h-100">
                  <div className="icon-wrapper">
                    <FontAwesomeIcon 
                      icon={industry.icon} 
                      aria-hidden="true"
                      style={{ fontSize: "2rem", color: "#030403" }}
                    />
                  </div>
                  <h3 className="industry-card-title">
                    {industry.title}
                  </h3>
                  <p className="industry-card-text">
                    {industry.description}
                  </p>
                  <div className="mt-3">
                    <h4 className="fs-6 text-uppercase mb-2" style={{ color: "#030403", fontSize: "0.875rem", letterSpacing: "1px", textAlign: "center" }}>Key Focus</h4>
                    <div className="d-flex flex-wrap gap-2 mb-4 justify-content-center">
                      {industry.technologies.split(', ').slice(0, 4).map((tech, i) => (
                        <span 
                          key={i} 
                          className="badge rounded-pill"
                          style={{
                            backgroundColor: "rgba(3, 196, 250, 0.2)",
                            color: "#030403",
                            border: "1px solid rgba(3, 196, 250, 0.4)",
                            padding: "0.4rem 0.75rem",
                            fontWeight: "500",
                            fontSize: "0.75rem"
                          }}
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-5 stat-row" style={{ backgroundColor: "rgba(0, 0, 0, 0.6)" }}>
        <div className="container py-4">
          <div className="row text-center">
            <div className="col-md-3 col-6 stat-item">
              <h2 className="display-5 fw-bold">10+</h2>
              <p style={{ color: "#ffffff", opacity: 0.9 }}>Industries Served</p>
            </div>
            <div className="col-md-3 col-6 stat-item">
              <h2 className="display-5 fw-bold">50+</h2>
              <p style={{ color: "#ffffff", opacity: 0.9 }}>Specialized Solutions</p>
            </div>
            <div className="col-md-3 col-6 stat-item">
              <h2 className="display-5 fw-bold">98%</h2>
              <p style={{ color: "#ffffff", opacity: 0.9 }}>Client Satisfaction</p>
            </div>
            <div className="col-md-3 col-6 stat-item">
              <h2 className="display-5 fw-bold">30+</h2>
              <p style={{ color: "#ffffff", opacity: 0.9 }}>Global Clients</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="cta-section">
        <div className="container text-center">
          <h2 className="display-4 fw-bold mb-4" style={{ fontFamily: "'Orbitron', sans-serif", background: "linear-gradient(135deg, #02e0f8, #d5e8ee)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            Transform Your Industry
          </h2>
          <p className="fs-5 mb-5 mx-auto" style={{ color: "#ffffff", maxWidth: "800px" }}>
            Partner with us to leverage technology that drives innovation in your sector.
          </p>
          <button className="cta-button">
            Get Started
          </button>
        </div>
      </section>
    </main>
  );
};

export default Industries;
